<template>
  <div class="responsive-drawer-wrapper">
    <div v-if="!isMobile" class="desktop-sidebar">
      <div class="sidebar-content">
        <div class="sidebar-header">
          <slot name="header-extra"></slot>
        </div>
        <div class="sidebar-body">
          <slot></slot>
        </div>
        <div v-if="$slots.footer" class="sidebar-footer">
          <slot name="footer"></slot>
        </div>
      </div>
    </div>

    <a-drawer
      v-if="isMobile"
      v-model:visible="visible"
      :placement="placement"
      :width="drawerWidth"
      :z-index="1000"
      :closable="false"
      :mask-closable="true"
      :header-style="{ padding: '0px' }"
      :body-style="{ padding: '0px' }"
      class="mobile-drawer"
    >
      <template #title>
        <slot name="header-extra"></slot>
      </template>
      <!-- Контент Drawer -->
      <div class="drawer-content">
        <slot></slot>
        <div v-if="$slots.footer" class="drawer-footer">
          <slot name="footer"> </slot>
        </div>
      </div>
    </a-drawer>
  </div>
</template>

<script setup lang="ts">
import { useUIStore } from '@/store/ui-store'
import { DrawerProps } from 'ant-design-vue'
import { storeToRefs } from 'pinia'
import { ref, onMounted, onUnmounted, watch } from 'vue'

const props = defineProps({
  title: {
    type: String,
    default: 'Меню',
  },
  width: {
    type: [String, Number],
    default: 250,
  },
  breakpoint: {
    type: String,
    default: 'md',
  },
})

const uiStore = useUIStore({ breakpoint: props.breakpoint })()
const { toggleDrawer, checkScreenSize } = uiStore
const { isDrawerOpen, isMobile } = storeToRefs(uiStore)

const placement = ref<DrawerProps['placement']>('left')

const emit = defineEmits(['open', 'close'])

const visible = ref(false)
const drawerWidth = ref(300)

drawerWidth.value = typeof props.width === 'number' ? props.width : parseInt(props.width)

const showDrawer = () => {
  visible.value = true
  emit('open')
}

const hideDrawer = () => {
  visible.value = false
  emit('close')
}

const handleResize = () => {
  checkScreenSize()
  if (!isMobile.value && visible.value) {
    visible.value = false
  }
}

onMounted(() => {
  checkScreenSize()
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

watch(
  () => isDrawerOpen.value,
  (newValue?: boolean) => {
    if (newValue) {
      showDrawer()
    } else {
      hideDrawer()
    }
  },
)

watch(
  () => visible.value,
  (newValue?: boolean) => {
    toggleDrawer(newValue || false)
  },
)

defineExpose({
  open: showDrawer,
  close: hideDrawer,
})
</script>

<style scoped>
.burger-button {
  margin: 18px 24px;
  cursor: pointer;
}
/* Десктопная версия */
.desktop-sidebar {
  position: fixed;
  top: 0;
  height: 100%;
  background: #fff;
  border-right: 1px solid #f0f0f0;
  transition: all 0.3s;
  z-index: 999;
}

.sidebar-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.sidebar-header {
  min-height: 56px;
  max-height: 56px;
  border-bottom: 1px solid #f0f0f0;
  background: #fff;
}

.sidebar-body {
  flex: 1;
  overflow-y: auto;
  padding: 0px;
}

.sidebar-footer {
  padding: 16px 24px;
  border-top: 1px solid #f0f0f0;
  background: #fff;
}

.mobile-menu-button {
  position: fixed;
  top: 16px;
  left: 16px;
  z-index: 999;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
}

.mobile-drawer :deep(.ant-drawer-body) {
  padding: 0;
}

.drawer-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.drawer-footer {
  margin-top: auto;
  padding: 16px;
  border-top: 1px solid #f0f0f0;
}

/* Адаптивные стили */
@media (max-width: 768px) {
  .desktop-sidebar {
    display: none;
  }

  .mobile-menu-button {
    display: block;
  }
}

@media (min-width: 769px) {
  .mobile-menu-button {
    display: none;
  }
}
</style>
